<template>
  <v-card data-testid="export_opticalgym_package">
    <v-card-title style="padding: 0px">
      <v-toolbar grey flat>
        <v-btn icon :disabled="exporting" @click="$emit('close')" data-testid="export_opticalgym_package_close">
          <v-icon>close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t("export_opticalgym_package.title") }}</v-toolbar-title>
      </v-toolbar>
    </v-card-title>

    <v-card-text class="pt-4">
      <v-progress-linear v-if="loading" indeterminate />
      <div v-else-if="sections.length === 0">
        {{ $t("export_opticalgym_package.empty") }}
      </div>
      <div v-else>
        <div v-for="section in sections" :key="section.entity_type">
          <v-subheader>
            <v-icon left small>{{ section.icon }}</v-icon>
            {{ section.title }}
          </v-subheader>
          <v-list>
            <v-list-item
              v-for="item in section.items"
              :key="item.key"
              :class="{ 'export-opticalgym-package-unexportable': item.unexportable }"
              :disabled="item.unexportable"
              @click="toggle(item)"
            >
              <v-list-item-action>
                <v-checkbox
                  :input-value="is_selected(item.key)"
                  :disabled="item.unexportable || is_locked(item.key)"
                  hide-details
                  class="mt-0"
                  data-testid="export_opticalgym_package_entity"
                  :data-entity-id="item.id"
                  :data-entity-type="item.entity_type"
                  @click.native.stop
                  @change="toggle(item)"
                />
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ item.title }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.unexportable ? $t("export_opticalgym_package.external_dependency") : item.description }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
      </div>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn text :disabled="exporting" @click="$emit('close')">
        {{ $t("common.cancel") }}
      </v-btn>
      <v-btn
        color="primary"
        :loading="exporting"
        :disabled="loading || exporting || effective_selected_keys.length === 0"
        @click="export_package"
        data-testid="export_opticalgym_package_export"
      >
        {{ $t("common.export") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import axios from "axios";

const SECTION_TYPES = [
  { entity_type: "grid_set", title_key: "community_sharing.grid_sets", icon: "fas fa-align-justify" },
  { entity_type: "grid_schedule", title_key: "community_sharing.grid_schedules", icon: "fas fa-border-all" },
  { entity_type: "grid_sequence", title_key: "community_sharing.grid_sequences", icon: "mdi-source-branch" },
  { entity_type: "video_exercise", title_key: "community_sharing.video_exercises", icon: "video_library" },
  { entity_type: "reading_text", title_key: "community_sharing.reading_texts", icon: "mdi-book-open-page-variant" },
  { entity_type: "file", title_key: "community_sharing.printable_sheets", icon: "print" },
];

function entity_key(entity_type, id) {
  return `${entity_type}:${id}`;
}

function video_clinic_ids(exercises) {
  if (!exercises) {
    return [];
  }
  const ids = [];
  for (const exercise of exercises) {
    if (exercise.exercise_type === "video/clinic" && exercise.exercise_id) {
      ids.push(exercise.exercise_id);
    }
  }
  return ids;
}

function raw_deps_for(entity, entity_type) {
  const deps = [];
  if (entity_type === "grid_set") {
    for (const id of video_clinic_ids(entity.exercises)) {
      deps.push({ entity_type: "video_exercise", id });
    }
  } else if (entity_type === "grid_schedule") {
    for (const stage of entity.grid || []) {
      for (const id of video_clinic_ids(stage.exercises)) {
        deps.push({ entity_type: "video_exercise", id });
      }
    }
    if (entity.next_grid_schedule) {
      deps.push({ entity_type: "grid_schedule", id: entity.next_grid_schedule });
    }
  } else if (entity_type === "grid_sequence") {
    for (const step of entity.sequence || []) {
      for (const id of video_clinic_ids(step.exercises)) {
        deps.push({ entity_type: "video_exercise", id });
      }
    }
  }
  return deps;
}

function normalize_entity(entity, entity_type) {
  return {
    key: entity_key(entity_type, entity.id),
    entity_type,
    id: entity.id,
    title: entity.title || "",
    description: entity.description || "",
    raw_deps: raw_deps_for(entity, entity_type),
    same_clinic_deps: [],
    has_external_dep: false,
    unexportable: false,
  };
}

function filename_from_disposition(header) {
  if (!header) {
    return null;
  }
  const utf_match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf_match) {
    try {
      return decodeURIComponent(utf_match[1]);
    } catch (_) {
      return utf_match[1];
    }
  }
  const quoted = header.match(/filename="([^"]+)"/i);
  if (quoted) {
    return quoted[1];
  }
  const unquoted = header.match(/filename=([^;]+)/i);
  if (unquoted) {
    return unquoted[1].trim();
  }
  return null;
}

export default {
  props: {
    clinic_id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      exporting: false,
      items: [],
      explicit_selected: [],
    };
  },
  computed: {
    entity_index() {
      const index = {};
      for (const item of this.items) {
        index[item.key] = item;
      }
      return index;
    },
    sections() {
      return SECTION_TYPES.map((section) => ({
        entity_type: section.entity_type,
        icon: section.icon,
        title: this.$t(section.title_key),
        items: this.items
          .filter((item) => item.entity_type === section.entity_type)
          .slice()
          .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" })),
      })).filter((section) => section.items.length > 0);
    },
    effective_selected_keys() {
      const selected = {};
      const visit = (key) => {
        if (selected[key]) {
          return;
        }
        const item = this.entity_index[key];
        if (!item || item.unexportable) {
          return;
        }
        selected[key] = true;
        for (const dep of item.same_clinic_deps) {
          visit(dep);
        }
      };
      for (const key of this.explicit_selected) {
        visit(key);
      }
      return Object.keys(selected);
    },
    locked_set() {
      const locked = {};
      const visit = (key) => {
        if (locked[key]) {
          return;
        }
        const item = this.entity_index[key];
        if (!item || item.unexportable) {
          return;
        }
        locked[key] = true;
        for (const dep of item.same_clinic_deps) {
          visit(dep);
        }
      };
      for (const key of this.explicit_selected) {
        const item = this.entity_index[key];
        if (!item) {
          continue;
        }
        for (const dep of item.same_clinic_deps) {
          visit(dep);
        }
      }
      return locked;
    },
  },
  watch: {
    clinic_id: {
      immediate: true,
      handler() {
        this.load_entities();
      },
    },
  },
  methods: {
    is_selected(key) {
      return this.effective_selected_keys.indexOf(key) !== -1;
    },
    is_locked(key) {
      return !!this.locked_set[key];
    },
    toggle(item) {
      if (item.unexportable || this.is_locked(item.key) || this.exporting) {
        return;
      }
      const index = this.explicit_selected.indexOf(item.key);
      if (index !== -1) {
        this.explicit_selected.splice(index, 1);
      } else {
        this.explicit_selected.push(item.key);
      }
    },
    finalize_items(items) {
      const keys = {};
      for (const item of items) {
        keys[item.key] = true;
      }

      for (const item of items) {
        const seen = {};
        item.same_clinic_deps = [];
        item.has_external_dep = false;
        for (const dep of item.raw_deps) {
          const dep_key = entity_key(dep.entity_type, dep.id);
          if (seen[dep_key]) {
            continue;
          }
          seen[dep_key] = true;
          if (keys[dep_key]) {
            item.same_clinic_deps.push(dep_key);
          } else {
            item.has_external_dep = true;
          }
        }
      }

      const index = {};
      for (const item of items) {
        index[item.key] = item;
      }

      const memo = {};
      const visiting = {};
      const is_unexportable = (key) => {
        if (Object.prototype.hasOwnProperty.call(memo, key)) {
          return memo[key];
        }
        if (visiting[key]) {
          return false;
        }
        const item = index[key];
        if (!item) {
          memo[key] = true;
          return true;
        }
        visiting[key] = true;
        let result = item.has_external_dep;
        if (!result) {
          for (const dep of item.same_clinic_deps) {
            if (is_unexportable(dep)) {
              result = true;
              break;
            }
          }
        }
        visiting[key] = false;
        memo[key] = result;
        return result;
      };

      for (const item of items) {
        item.unexportable = is_unexportable(item.key);
        delete item.raw_deps;
      }

      this.items = items;
    },
    response_error(response, fallback) {
      return response?.data?.error_message || fallback;
    },
    async load_entities() {
      this.loading = true;
      this.explicit_selected = [];
      this.items = [];

      try {
        const [grids, videos, texts, files] = await Promise.all([
          axios.get("/api/grid/clinic/" + this.clinic_id),
          axios.get("/api/clinic/" + this.clinic_id + "/video_exercises"),
          axios.get("/api/clinic/" + this.clinic_id + "/reading_texts"),
          axios.get("/api/clinic/" + this.clinic_id + "/files/printable"),
        ]);

        if (grids.data.status !== "success") {
          window.messageBus.$emit("error", this.response_error(grids, "Error fetching grids"));
          return;
        }
        if (videos.data.status !== "success") {
          window.messageBus.$emit("error", this.response_error(videos, "Error fetching video exercises"));
          return;
        }
        if (texts.data.status !== "success") {
          window.messageBus.$emit("error", this.response_error(texts, "Error fetching reading texts"));
          return;
        }
        if (files.data.status !== "success") {
          window.messageBus.$emit("error", this.response_error(files, "Error fetching printable sheets"));
          return;
        }

        const items = [];
        for (const entity of grids.data.data.grid_sets || []) {
          items.push(normalize_entity(entity, "grid_set"));
        }
        for (const entity of grids.data.data.grid_schedules || []) {
          items.push(normalize_entity(entity, "grid_schedule"));
        }
        for (const entity of grids.data.data.grid_sequences || []) {
          items.push(normalize_entity(entity, "grid_sequence"));
        }
        for (const entity of videos.data.data || []) {
          items.push(normalize_entity(entity, "video_exercise"));
        }
        for (const entity of texts.data.data || []) {
          items.push(normalize_entity(entity, "reading_text"));
        }
        for (const entity of files.data.data || []) {
          items.push(normalize_entity(entity, "file"));
        }

        this.finalize_items(items);
      } catch (error) {
        window.messageBus.$emit("error", error?.message || "Error loading clinic content");
      } finally {
        this.loading = false;
      }
    },
    async blob_error_message(blob, fallback) {
      try {
        const text = await blob.text();
        const json = JSON.parse(text);
        return json.error_message || fallback;
      } catch (_) {
        return fallback;
      }
    },
    async export_package() {
      const entities = this.effective_selected_keys
        .map((key) => this.entity_index[key])
        .filter((item) => item)
        .map((item) => ({
          entity_type: item.entity_type,
          id: item.id,
        }));

      if (entities.length === 0) {
        return;
      }

      this.exporting = true;
      try {
        const response = await axios.post(
          "/api/opticalgym/export",
          { entities },
          { responseType: "blob" }
        );

        const content_type = (response.headers["content-type"] || "").toLowerCase();
        const is_zip = content_type.includes("zip") || content_type.includes("octet-stream");
        if (response.status >= 400 || !is_zip) {
          const message = await this.blob_error_message(
            response.data,
            this.$t("export_opticalgym_package.export_failed")
          );
          window.messageBus.$emit("error", message);
          return;
        }

        const filename =
          filename_from_disposition(response.headers["content-disposition"]) || "export.opticalgym";
        const url = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        window.messageBus.$emit("error", error?.message || this.$t("export_opticalgym_package.export_failed"));
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.export-opticalgym-package-unexportable {
  opacity: 0.5;
}
.export-opticalgym-package-unexportable .v-list-item__title,
.export-opticalgym-package-unexportable .v-list-item__subtitle {
  color: rgba(0, 0, 0, 0.38) !important;
}
</style>
